<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query';

import { getApiGetPaymentMethodsQueryKey, type PaymentMethodEntity, useApiUpdateCardPaymentMethod } from '~/apiClient';
import { isCardPaymentMethod } from '~/types/paymentMethod.type';

// PROPS
const { paymentMethod } = defineProps({
    paymentMethod: { type: Object as PropType<PaymentMethodEntity>, required: true },
});

// MODEL
const open = defineModel<boolean>('open', { required: true });

// COMPOSABLES
const { isMobile } = useResponsive();
const { tt } = useTypedI18n();
const { mutateAsync, isPending } = useApiUpdateCardPaymentMethod();
const queryClient = useQueryClient();
const { notifyBackendError, notifyGenericError } = useNotification();
const { paymentMethodLabel } = usePaymentMethodInfo(() => paymentMethod);

// DATA
const monthsOptions = range(1, 12, 1).map(value => ({ label: String(value).padStart(2, '0'), value }));
const currentYear = new Date().getFullYear();
const yearsOptions = range(currentYear, currentYear + 15, 1).map(value => ({ label: String(value), value }));
const selectedMonth = ref(1);
const selectedYear = ref(currentYear);

// METHODS
const initSelectedValues = (): void => {
    if (!isCardPaymentMethod(paymentMethod)) return;

    selectedMonth.value = paymentMethod.card.expMonth;
    selectedYear.value = paymentMethod.card.expYear;
};

const closeModal = (): void => {
    open.value = false;
};

const onConfirm = (): void => {
    mutateAsync({ paymentMethodId: paymentMethod.id, data: { expirationMonth: selectedMonth.value, expirationYear: selectedYear.value } })
        .then(() => {
            queryClient.refetchQueries({ queryKey: getApiGetPaymentMethodsQueryKey(), type: 'active' });
            closeModal();
        })
        .catch(error => {
            if (isBackendError(error.data)) {
                notifyBackendError({ error: error.data.error });
            } else {
                notifyGenericError();
            }
        });
};

// WATCHERS
watch(open, (value, oldValue) => value && !oldValue && initSelectedValues());
</script>

<template>
    <UModal v-model:open="open" :fullscreen="isMobile" :title="tt('paymentMethods.updateExpirationModal.title')" v-bind="modalNoDescAriaFixupProps">
        <template #body>
            <div class="space-y-6">
                <p class="text-primary text-center text-lg font-semibold uppercase">{{ paymentMethodLabel }}</p>
                <div class="flex justify-center gap-3">
                    <UFormField :label="tt('paymentMethods.updateExpirationModal.month')">
                        <USelectMenu v-model="selectedMonth" :search-input="false" :items="monthsOptions" value-key="value" />
                    </UFormField>
                    <UFormField :label="tt('paymentMethods.updateExpirationModal.year')">
                        <USelectMenu v-model="selectedYear" :search-input="false" :items="yearsOptions" value-key="value" />
                    </UFormField>
                </div>
            </div>
        </template>
        <template #footer="{ close }">
            <UButton block size="xl" :label="tt('paymentMethods.updateExpirationModal.confirmButton')" :loading="isPending" @click="onConfirm" />
            <UButton block size="xl" variant="link" color="neutral" :label="tt('paymentMethods.updateExpirationModal.cancelButton')" @click="close" />
        </template>
    </UModal>
</template>
